import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { AppRoot } from '@telegram-apps/telegram-ui';
import '@telegram-apps/telegram-ui/dist/styles.css';
import '../../../packages/design-tokens/fitbot.css';
import './styles/overrides.css';

import { queryClient } from './lib/queryClient';
import App from './App';

function DynamicAppRoot({ children }: { children: React.ReactNode }) {
  const WebApp = (window as any).Telegram?.WebApp;
  const [theme, setTheme] = useState<'light'>(() => 'light');

  useEffect(() => {
    if (!WebApp) return;
    const applyFitBotTheme = () => {
      setTheme('light');
      WebApp.setHeaderColor?.('#fbfaf5');
      WebApp.setBackgroundColor?.('#fbfaf5');
      WebApp.setBottomBarColor?.('#fbfaf5');
    };
    applyFitBotTheme();
    const updateTheme = () => applyFitBotTheme();
    WebApp.onEvent('themeChanged', updateTheme);
    return () => WebApp.offEvent('themeChanged', updateTheme);
  }, []);

  return (
    <AppRoot appearance={theme}>
      <div className="fitbot-miniapp-root">{children}</div>
    </AppRoot>
  );
}

if (window.Telegram?.WebApp) {
  window.Telegram.WebApp.ready();
  window.Telegram.WebApp.expand();

  // Force cache-bust on every cold start of Mini App.
  // This prevents WebView from serving stale bundle when we navigate to / after changes.
  if (typeof window !== 'undefined' && 'caches' in window) {
    window.caches
      .keys()
      .then((keys) => {
        keys.forEach((k) => window.caches.delete(k));
      })
      .catch(() => {});
  }
}

const root = document.getElementById('root');
if (!root) throw new Error('Root not found');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <DynamicAppRoot>
        <App />
      </DynamicAppRoot>
    </QueryClientProvider>
  </StrictMode>
);
